import React, { useState } from 'react';
import { DollarSignIcon, LayersIcon, UserIcon, UsersIcon, BuildingIcon, HomeIcon, TagIcon, GitBranchIcon, ArrowUpRightIcon, AlertTriangleIcon, ClockIcon, EyeIcon, CheckCircleIcon, LockIcon, CalendarIcon, PercentIcon, HelpCircleIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
type FDOAMode = 'Override' | 'Satisfy' | 'CostCentreOnly';
type FDOARuleProps = {
  rule: any;
  onSave: (rule: any) => void;
  onCancel: () => void;
  locale: string;
};
export function FDOARuleEditor({
  rule,
  onSave,
  onCancel,
  locale
}: FDOARuleProps) {
  // Initialize state with rule data or defaults
  const [ruleData, setRuleData] = useState({
    ...rule,
    modes: rule.modes || {
      vendorMode: 'Satisfy' as FDOAMode,
      siteMode: 'Satisfy' as FDOAMode,
      entityMode: 'Override' as FDOAMode
    },
    delegation: rule.delegation || {
      inheritUpperLimit: true,
      useOriginalChain: true,
      maxHops: 5
    },
    fourEyes: rule.fourEyes || {
      enabled: true,
      threshold: 10000,
      applyAt: 'Both',
      currency: 'AUD'
    },
    emergency: rule.emergency || {
      enabled: false,
      approvers: []
    },
    lineLevel: rule.lineLevel || {
      enabled: true,
      evaluationMode: 'Sequential',
      categoriesOnly: true
    },
    preApproval: rule.preApproval || {
      checkClosedPeriods: true
    },
    multiCurrency: rule.multiCurrency || {
      enabled: true,
      baseCurrency: 'AUD',
      evaluateAt: 'Header'
    },
    audit: rule.audit || {
      enabled: true,
      storeDecisions: true
    }
  });
  // Sample data for dropdowns
  const [entities] = useState([{
    id: 'entity-001',
    name: 'Global Technologies Inc.'
  }, {
    id: 'entity-002',
    name: 'European Operations Ltd'
  }, {
    id: 'entity-003',
    name: 'APAC Subsidiary'
  }]);
  const [users] = useState([{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }]);
  const [roles] = useState([{
    id: 'role-001',
    name: 'Finance Manager'
  }, {
    id: 'role-002',
    name: 'Department Head'
  }, {
    id: 'role-003',
    name: 'CEO'
  }, {
    id: 'role-004',
    name: 'CFO'
  }]);
  const [glCategories] = useState([{
    id: 'cat-001',
    code: '1000',
    description: 'IT Equipment',
    entityId: 'entity-001'
  }, {
    id: 'cat-002',
    code: '2000',
    description: 'Office Supplies',
    entityId: 'entity-001'
  }, {
    id: 'cat-003',
    code: '3000',
    description: 'Professional Services',
    entityId: 'entity-001'
  }, {
    id: 'cat-004',
    code: '4000',
    description: 'Travel Expenses',
    entityId: 'entity-001'
  }]);
  const [currencies] = useState([{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }]);
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    defaults: true,
    modes: true,
    delegation: true,
    categories: true,
    emergency: false,
    fourEyes: true,
    lineLevel: false,
    periods: false,
    multiCurrency: false,
    audit: false
  });
  const [defaultApprovers, setDefaultApprovers] = useState(rule.defaultApprovers || [{
    id: 'default-001',
    scopeType: 'Entity',
    scopeId: 'entity-001',
    userOrRoleId: 'role-001',
    isReviewer: false,
    isApprover: true,
    fdoaLimit: 10000,
    currency: 'AUD',
    belongsToCategories: ['cat-001', 'cat-002']
  }, {
    id: 'default-002',
    scopeType: 'Vendor',
    scopeId: 'vendor-001',
    userOrRoleId: 'user-002',
    isReviewer: true,
    isApprover: false,
    fdoaLimit: 0,
    currency: 'AUD',
    belongsToCategories: []
  }]);
  const [categoryApprovers, setCategoryApprovers] = useState(rule.categoryApprovers || [{
    id: 'catapp-001',
    categoryId: 'cat-001',
    sequence: 1,
    userOrRoleId: 'user-001',
    limit: 5000,
    currency: 'AUD'
  }, {
    id: 'catapp-002',
    categoryId: 'cat-001',
    sequence: 2,
    userOrRoleId: 'role-001',
    limit: 25000,
    currency: 'AUD'
  }, {
    id: 'catapp-003',
    categoryId: 'cat-001',
    sequence: 3,
    userOrRoleId: 'role-003',
    limit: 100000,
    currency: 'AUD'
  }]);
  const [accountingPeriods, setAccountingPeriods] = useState(rule.accountingPeriods || [{
    id: 'period-001',
    entityId: 'entity-001',
    periodCode: 'FY23-Q3',
    startUtc: '2023-07-01T00:00:00Z',
    endUtc: '2023-09-30T23:59:59Z',
    isClosed: true
  }, {
    id: 'period-002',
    entityId: 'entity-001',
    periodCode: 'FY23-Q4',
    startUtc: '2023-10-01T00:00:00Z',
    endUtc: '2023-12-31T23:59:59Z',
    isClosed: false
  }]);
  // Helper functions
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleModeChange = (scope: 'vendorMode' | 'siteMode' | 'entityMode', mode: FDOAMode) => {
    setRuleData({
      ...ruleData,
      modes: {
        ...ruleData.modes,
        [scope]: mode
      }
    });
  };
  const handleDelegationChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      delegation: {
        ...ruleData.delegation,
        [field]: value
      }
    });
  };
  const handleFourEyesChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      fourEyes: {
        ...ruleData.fourEyes,
        [field]: value
      }
    });
  };
  const handleEmergencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        [field]: value
      }
    });
  };
  const handleLineLevelChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      lineLevel: {
        ...ruleData.lineLevel,
        [field]: value
      }
    });
  };
  const handleMultiCurrencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      multiCurrency: {
        ...ruleData.multiCurrency,
        [field]: value
      }
    });
  };
  const addDefaultApprover = () => {
    const newApprover = {
      id: `default-${Date.now()}`,
      scopeType: 'Entity',
      scopeId: '',
      userOrRoleId: '',
      isReviewer: false,
      isApprover: true,
      fdoaLimit: 0,
      currency: 'AUD',
      belongsToCategories: []
    };
    setDefaultApprovers([...defaultApprovers, newApprover]);
  };
  const updateDefaultApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setDefaultApprovers(updatedApprovers);
  };
  const removeDefaultApprover = (index: number) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers.splice(index, 1);
    setDefaultApprovers(updatedApprovers);
  };
  const addCategoryApprover = () => {
    const newApprover = {
      id: `catapp-${Date.now()}`,
      categoryId: '',
      sequence: categoryApprovers.length + 1,
      userOrRoleId: '',
      limit: 0,
      currency: 'AUD'
    };
    setCategoryApprovers([...categoryApprovers, newApprover]);
  };
  const updateCategoryApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setCategoryApprovers(updatedApprovers);
  };
  const removeCategoryApprover = (index: number) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers.splice(index, 1);
    setCategoryApprovers(updatedApprovers);
  };
  const addEmergencyApprover = (userOrRoleId: string) => {
    if (!ruleData.emergency.approvers.includes(userOrRoleId)) {
      setRuleData({
        ...ruleData,
        emergency: {
          ...ruleData.emergency,
          approvers: [...ruleData.emergency.approvers, userOrRoleId]
        }
      });
    }
  };
  const removeEmergencyApprover = (userOrRoleId: string) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        approvers: ruleData.emergency.approvers.filter(id => id !== userOrRoleId)
      }
    });
  };
  const addAccountingPeriod = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const newPeriod = {
      id: `period-${Date.now()}`,
      entityId: 'entity-001',
      periodCode: `FY${today.getFullYear().toString().substr(2)}-Q${Math.floor(today.getMonth() / 3) + 1}`,
      startUtc: today.toISOString(),
      endUtc: nextMonth.toISOString(),
      isClosed: false
    };
    setAccountingPeriods([...accountingPeriods, newPeriod]);
  };
  const updateAccountingPeriod = (index: number, field: string, value: any) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods[index] = {
      ...updatedPeriods[index],
      [field]: value
    };
    setAccountingPeriods(updatedPeriods);
  };
  const removeAccountingPeriod = (index: number) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods.splice(index, 1);
    setAccountingPeriods(updatedPeriods);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedRule = {
      ...rule,
      ...ruleData,
      defaultApprovers,
      categoryApprovers,
      accountingPeriods
    };
    onSave(updatedRule);
  };
  // Render the form
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-11225">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-11226">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center" data-id="element-11227">
          <div data-id="element-11228">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11229">
              Financial Delegation Of Authority (FDOA) Rule Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-11230">
              Configure approval pathways based on financial delegation limits
              and cost centers
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-11231">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11232">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11233">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-11234" />
              Save Rule
            </button>
          </div>
        </div>
        {/* Basic Information */}
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-11235">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-11236">
            Basic Information
          </h4>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11237">
            <div className="sm:col-span-3" data-id="element-11238">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-11239">
                Rule Name
              </label>
              <div className="mt-1" data-id="element-11240">
                <input type="text" id="name" value={ruleData.name} onChange={e => setRuleData({
                ...ruleData,
                name: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11241" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11242">
              <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-11243">
                Version
              </label>
              <div className="mt-1" data-id="element-11244">
                <input type="text" id="version" value={ruleData.version} onChange={e => setRuleData({
                ...ruleData,
                version: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11245" />
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-11246">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-11247">
                Description
              </label>
              <div className="mt-1" data-id="element-11248">
                <textarea id="description" rows={2} value={ruleData.description} onChange={e => setRuleData({
                ...ruleData,
                description: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11249" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11250">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-11251">
                Status
              </label>
              <div className="mt-1" data-id="element-11252">
                <select id="status" value={ruleData.status} onChange={e => setRuleData({
                ...ruleData,
                status: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11253">
                  <option value="Draft" data-id="element-11254">Draft</option>
                  <option value="Active" data-id="element-11255">Active</option>
                  <option value="Inactive" data-id="element-11256">Inactive</option>
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11257">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-11258">
                Scope
              </label>
              <div className="mt-1" data-id="element-11259">
                <select id="scope" value={ruleData.scope} onChange={e => setRuleData({
                ...ruleData,
                scope: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11260">
                  <option value="Entity" data-id="element-11261">Entity</option>
                  <option value="Global" data-id="element-11262">Global</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Default Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-11263">
          <button type="button" onClick={() => toggleSection('defaults')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11264">
            <div className="flex items-center" data-id="element-11265">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11266" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11267">
                Default Approvers (Vendor/Site/Entity)
              </h3>
            </div>
            {expandedSections.defaults ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11268" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11269" />}
          </button>
          {expandedSections.defaults && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11270">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11271">
                Configure default approvers for vendors, sites, and entities.
                These can be reviewers, approvers, or both.
              </p>
              <div className="space-y-4" data-id="element-11272">
                {defaultApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-11273">
                    <div className="flex justify-between items-start" data-id="element-11274">
                      <div className="flex items-center" data-id="element-11275">
                        {approver.scopeType === 'Entity' ? <BuildingIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-11276" /> : approver.scopeType === 'Site' ? <HomeIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-11277" /> : <TagIcon className="h-5 w-5 text-orange-500 mr-2" data-id="element-11278" />}
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-11279">
                          {approver.scopeType} Default Approver
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeDefaultApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-11280">
                        <TrashIcon className="h-4 w-4" data-id="element-11281" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11282">
                      <div className="sm:col-span-2" data-id="element-11283">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11284">
                          Scope Type
                        </label>
                        <select value={approver.scopeType} onChange={e => updateDefaultApprover(index, 'scopeType', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11285">
                          <option value="Entity" data-id="element-11286">Entity</option>
                          <option value="Site" data-id="element-11287">Site</option>
                          <option value="Vendor" data-id="element-11288">Vendor</option>
                        </select>
                      </div>
                      <div className="sm:col-span-4" data-id="element-11289">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11290">
                          {approver.scopeType} Name
                        </label>
                        <select value={approver.scopeId} onChange={e => updateDefaultApprover(index, 'scopeId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11291">
                          <option value="" data-id="element-11292">Select {approver.scopeType}</option>
                          {approver.scopeType === 'Entity' && entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-11293">
                                {entity.name}
                              </option>)}
                          {/* Add similar options for Site and Vendor when available */}
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-11294">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11295">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateDefaultApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11296">
                          <option value="" data-id="element-11297">Select User or Role</option>
                          <optgroup label="Users" data-id="element-11298">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-11299">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-11300">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-11301">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-11302">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11303">
                          Approval Type
                        </label>
                        <div className="mt-2 flex items-center space-x-4" data-id="element-11304">
                          <div className="flex items-center" data-id="element-11305">
                            <input id={`reviewer-${approver.id}`} type="checkbox" checked={approver.isReviewer} onChange={e => updateDefaultApprover(index, 'isReviewer', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11306" />
                            <label htmlFor={`reviewer-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-11307">
                              Reviewer
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-11308">
                            <input id={`approver-${approver.id}`} type="checkbox" checked={approver.isApprover} onChange={e => updateDefaultApprover(index, 'isApprover', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11309" />
                            <label htmlFor={`approver-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-11310">
                              Approver
                            </label>
                          </div>
                        </div>
                      </div>
                      {approver.isApprover && <>
                          <div className="sm:col-span-2" data-id="element-11311">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-11312">
                              FDOA Limit
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm" data-id="element-11313">
                              <input type="number" value={approver.fdoaLimit} onChange={e => updateDefaultApprover(index, 'fdoaLimit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-11314" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-11315">
                                <span className="text-gray-500 sm:text-sm" data-id="element-11316">
                                  {approver.currency}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="sm:col-span-2" data-id="element-11317">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-11318">
                              Currency
                            </label>
                            <select value={approver.currency} onChange={e => updateDefaultApprover(index, 'currency', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11319">
                              {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11320">
                                  {currency.code}
                                </option>)}
                            </select>
                          </div>
                        </>}
                      <div className="sm:col-span-6" data-id="element-11321">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11322">
                          Belongs to Categories (Optional)
                        </label>
                        <select multiple value={approver.belongsToCategories} onChange={e => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    updateDefaultApprover(index, 'belongsToCategories', selected);
                  }} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" size={3} data-id="element-11323">
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-11324">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-11325">
                          Hold Ctrl/Cmd to select multiple categories
                        </p>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addDefaultApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11326">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-11327" />
                  Add Default Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Modes Section */}
        <div className="border-b border-gray-200" data-id="element-11328">
          <button type="button" onClick={() => toggleSection('modes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11329">
            <div className="flex items-center" data-id="element-11330">
              <GitBranchIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11331" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11332">
                Approval Modes
              </h3>
            </div>
            {expandedSections.modes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11333" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11334" />}
          </button>
          {expandedSections.modes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11335">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11336">
                Configure how default approvers interact with cost center
                approvers
              </p>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-11337">
                <div data-id="element-11338">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11339">
                    Vendor Mode
                  </label>
                  <div className="space-y-2" data-id="element-11340">
                    <div className="flex items-center" data-id="element-11341">
                      <input id="vendor-override" type="radio" checked={ruleData.modes.vendorMode === 'Override'} onChange={() => handleModeChange('vendorMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11342" />
                      <label htmlFor="vendor-override" className="ml-3 block text-sm text-gray-700" data-id="element-11343">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11344">
                      <input id="vendor-satisfy" type="radio" checked={ruleData.modes.vendorMode === 'Satisfy'} onChange={() => handleModeChange('vendorMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11345" />
                      <label htmlFor="vendor-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-11346">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11347">
                      <input id="vendor-costcentre" type="radio" checked={ruleData.modes.vendorMode === 'CostCentreOnly'} onChange={() => handleModeChange('vendorMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11348" />
                      <label htmlFor="vendor-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-11349">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-11350">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11351">
                    Site Mode
                  </label>
                  <div className="space-y-2" data-id="element-11352">
                    <div className="flex items-center" data-id="element-11353">
                      <input id="site-override" type="radio" checked={ruleData.modes.siteMode === 'Override'} onChange={() => handleModeChange('siteMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11354" />
                      <label htmlFor="site-override" className="ml-3 block text-sm text-gray-700" data-id="element-11355">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11356">
                      <input id="site-satisfy" type="radio" checked={ruleData.modes.siteMode === 'Satisfy'} onChange={() => handleModeChange('siteMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11357" />
                      <label htmlFor="site-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-11358">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11359">
                      <input id="site-costcentre" type="radio" checked={ruleData.modes.siteMode === 'CostCentreOnly'} onChange={() => handleModeChange('siteMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11360" />
                      <label htmlFor="site-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-11361">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-11362">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11363">
                    Entity Mode
                  </label>
                  <div className="space-y-2" data-id="element-11364">
                    <div className="flex items-center" data-id="element-11365">
                      <input id="entity-override" type="radio" checked={ruleData.modes.entityMode === 'Override'} onChange={() => handleModeChange('entityMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11366" />
                      <label htmlFor="entity-override" className="ml-3 block text-sm text-gray-700" data-id="element-11367">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11368">
                      <input id="entity-satisfy" type="radio" checked={ruleData.modes.entityMode === 'Satisfy'} onChange={() => handleModeChange('entityMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11369" />
                      <label htmlFor="entity-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-11370">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11371">
                      <input id="entity-costcentre" type="radio" checked={ruleData.modes.entityMode === 'CostCentreOnly'} onChange={() => handleModeChange('entityMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11372" />
                      <label htmlFor="entity-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-11373">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-gray-50 p-4 rounded-md" data-id="element-11374">
                <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11375">
                  Mode Explanations:
                </h5>
                <ul className="text-xs text-gray-600 space-y-2" data-id="element-11376">
                  <li data-id="element-11377">
                    <span className="font-semibold" data-id="element-11378">Override:</span> Default
                    approvers at this level will replace cost centre approvers
                  </li>
                  <li data-id="element-11379">
                    <span className="font-semibold" data-id="element-11380">Satisfy:</span> Default
                    approvers count as part of category sequence if they belong
                    to the category
                  </li>
                  <li data-id="element-11381">
                    <span className="font-semibold" data-id="element-11382">Cost Centre Only:</span>{' '}
                    Ignore default approvers at this level, use only cost centre
                    approvers
                  </li>
                </ul>
              </div>
            </div>}
        </div>
        {/* Delegation & Escalation Section */}
        <div className="border-b border-gray-200" data-id="element-11383">
          <button type="button" onClick={() => toggleSection('delegation')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11384">
            <div className="flex items-center" data-id="element-11385">
              <ArrowUpRightIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11386" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11387">
                Delegation & Escalation
              </h3>
            </div>
            {expandedSections.delegation ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11388" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11389" />}
          </button>
          {expandedSections.delegation && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11390">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11391">
                Configure delegation and escalation settings
              </p>
              <div className="space-y-4" data-id="element-11392">
                <div className="flex items-center" data-id="element-11393">
                  <input id="inherit-limit" type="checkbox" checked={ruleData.delegation.inheritUpperLimit} onChange={e => handleDelegationChange('inheritUpperLimit', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11394" />
                  <label htmlFor="inherit-limit" className="ml-3 block text-sm text-gray-700" data-id="element-11395">
                    Delegate inherits original approver's upper limit
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, a delegate will use the original approver's FDOA limit instead of their own" data-id="element-11396" />
                </div>
                <div className="flex items-center" data-id="element-11397">
                  <input id="original-chain" type="checkbox" checked={ruleData.delegation.useOriginalChain} onChange={e => handleDelegationChange('useOriginalChain', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11398" />
                  <label htmlFor="original-chain" className="ml-3 block text-sm text-gray-700" data-id="element-11399">
                    Escalate along original approver's manager chain
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, escalation will follow the original approver's management chain rather than the delegate's" data-id="element-11400" />
                </div>
                <div data-id="element-11401">
                  <label htmlFor="max-hops" className="block text-sm font-medium text-gray-700" data-id="element-11402">
                    Maximum Escalation Hops
                  </label>
                  <div className="mt-1 w-24" data-id="element-11403">
                    <input type="number" id="max-hops" min="1" max="10" value={ruleData.delegation.maxHops} onChange={e => handleDelegationChange('maxHops', parseInt(e.target.value))} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11404" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-11405">
                    Maximum number of manager levels to escalate through
                  </p>
                </div>
              </div>
            </div>}
        </div>
        {/* Category Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-11406">
          <button type="button" onClick={() => toggleSection('categories')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11407">
            <div className="flex items-center" data-id="element-11408">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11409" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11410">
                Category Approvers
              </h3>
            </div>
            {expandedSections.categories ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11411" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11412" />}
          </button>
          {expandedSections.categories && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11413">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11414">
                Configure approvers for each GL category (cost centre)
              </p>
              <div className="space-y-4" data-id="element-11415">
                {categoryApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-11416">
                    <div className="flex justify-between items-start" data-id="element-11417">
                      <div className="flex items-center" data-id="element-11418">
                        <LayersIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-11419" />
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-11420">
                          Category Approver (Sequence {approver.sequence})
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeCategoryApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-11421">
                        <TrashIcon className="h-4 w-4" data-id="element-11422" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11423">
                      <div className="sm:col-span-3" data-id="element-11424">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11425">
                          GL Category
                        </label>
                        <select value={approver.categoryId} onChange={e => updateCategoryApprover(index, 'categoryId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11426">
                          <option value="" data-id="element-11427">Select GL Category</option>
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-11428">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-1" data-id="element-11429">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11430">
                          Sequence
                        </label>
                        <input type="number" min="1" value={approver.sequence} onChange={e => updateCategoryApprover(index, 'sequence', parseInt(e.target.value))} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11431" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-11432">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11433">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateCategoryApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11434">
                          <option value="" data-id="element-11435">Select User or Role</option>
                          <optgroup label="Users" data-id="element-11436">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-11437">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-11438">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-11439">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-11440">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11441">
                          Approval Limit
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-11442">
                          <input type="number" value={approver.limit} onChange={e => updateCategoryApprover(index, 'limit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-11443" />
                          <select value={approver.currency} onChange={e => updateCategoryApprover(index, 'currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-11444">
                            {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11445">
                                {currency.code}
                              </option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addCategoryApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11446">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-11447" />
                  Add Category Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Four-Eyes Section */}
        <div className="border-b border-gray-200" data-id="element-11448">
          <button type="button" onClick={() => toggleSection('fourEyes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11449">
            <div className="flex items-center" data-id="element-11450">
              <EyeIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11451" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11452">
                Four-Eyes Principle
              </h3>
            </div>
            {expandedSections.fourEyes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11453" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11454" />}
          </button>
          {expandedSections.fourEyes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11455">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11456">
                Configure Four-Eyes principle requiring two distinct approvers
              </p>
              <div className="flex items-center mb-4" data-id="element-11457">
                <input id="enable-four-eyes" type="checkbox" checked={ruleData.fourEyes.enabled} onChange={e => handleFourEyesChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11458" />
                <label htmlFor="enable-four-eyes" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11459">
                  Enable Four-Eyes Principle
                </label>
              </div>
              {ruleData.fourEyes.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-11460">
                  <div data-id="element-11461">
                    <label htmlFor="four-eyes-threshold" className="block text-sm font-medium text-gray-700" data-id="element-11462">
                      Threshold
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm" data-id="element-11463">
                      <input type="number" id="four-eyes-threshold" value={ruleData.fourEyes.threshold} onChange={e => handleFourEyesChange('threshold', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-11464" />
                      <select value={ruleData.fourEyes.currency} onChange={e => handleFourEyesChange('currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-11465">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11466">
                            {currency.code}
                          </option>)}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-11467">
                      Four-Eyes principle applies to transactions above this
                      amount
                    </p>
                  </div>
                  <div data-id="element-11468">
                    <label htmlFor="four-eyes-apply" className="block text-sm font-medium text-gray-700" data-id="element-11469">
                      Apply At
                    </label>
                    <select id="four-eyes-apply" value={ruleData.fourEyes.applyAt} onChange={e => handleFourEyesChange('applyAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11470">
                      <option value="Header" data-id="element-11471">Header</option>
                      <option value="Line" data-id="element-11472">Line</option>
                      <option value="Both" data-id="element-11473">Both</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-11474">
                      Where to apply the Four-Eyes check
                    </p>
                  </div>
                </div>}
              <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-11475">
                <div className="flex" data-id="element-11476">
                  <div className="flex-shrink-0" data-id="element-11477">
                    <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-11478" />
                  </div>
                  <div className="ml-3" data-id="element-11479">
                    <h3 className="text-sm font-medium text-yellow-800" data-id="element-11480">
                      Four-Eyes Principle Note
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700" data-id="element-11481">
                      <p data-id="element-11482">When enabled, Four-Eyes requires that:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-11483">
                        <li data-id="element-11484">
                          Two distinct approvers must approve transactions above
                          the threshold
                        </li>
                        <li data-id="element-11485">
                          The requester/coder of a document cannot be one of its
                          approvers
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Emergency Approvals Section */}
        <div className="border-b border-gray-200" data-id="element-11486">
          <button type="button" onClick={() => toggleSection('emergency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11487">
            <div className="flex items-center" data-id="element-11488">
              <AlertTriangleIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11489" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11490">
                Emergency Approvals
              </h3>
            </div>
            {expandedSections.emergency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11491" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11492" />}
          </button>
          {expandedSections.emergency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11493">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11494">
                Configure emergency approvers who can short-circuit normal
                approval flows
              </p>
              <div className="flex items-center mb-4" data-id="element-11495">
                <input id="enable-emergency" type="checkbox" checked={ruleData.emergency.enabled} onChange={e => handleEmergencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11496" />
                <label htmlFor="enable-emergency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11497">
                  Enable Emergency Approvals
                </label>
              </div>
              {ruleData.emergency.enabled && <div data-id="element-11498">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11499">
                    Emergency Approvers
                  </label>
                  <div className="mb-4" data-id="element-11500">
                    <select id="emergency-approver-select" onChange={e => {
                if (e.target.value) {
                  addEmergencyApprover(e.target.value);
                  e.target.value = '';
                }
              }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11501">
                      <option value="" data-id="element-11502">Add Emergency Approver</option>
                      <optgroup label="Users" data-id="element-11503">
                        {users.filter(user => !ruleData.emergency.approvers.includes(user.id)).map(user => <option key={user.id} value={user.id} data-id="element-11504">
                              {user.name}
                            </option>)}
                      </optgroup>
                      <optgroup label="Roles" data-id="element-11505">
                        {roles.filter(role => !ruleData.emergency.approvers.includes(role.id)).map(role => <option key={role.id} value={role.id} data-id="element-11506">
                              {role.name}
                            </option>)}
                      </optgroup>
                    </select>
                  </div>
                  {ruleData.emergency.approvers.length > 0 ? <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-11507">
                      {ruleData.emergency.approvers.map(approverId => {
                const user = users.find(u => u.id === approverId);
                const role = roles.find(r => r.id === approverId);
                const name = user?.name || role?.name || 'Unknown';
                const isUser = !!user;
                return <li key={approverId} className="px-4 py-3 flex items-center justify-between" data-id="element-11508">
                            <div className="flex items-center" data-id="element-11509">
                              {isUser ? <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-11510" /> : <UsersIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-11511" />}
                              <span className="text-sm text-gray-900" data-id="element-11512">
                                {name}
                              </span>
                            </div>
                            <button type="button" onClick={() => removeEmergencyApprover(approverId)} className="text-red-600 hover:text-red-900" data-id="element-11513">
                              <TrashIcon className="h-4 w-4" data-id="element-11514" />
                            </button>
                          </li>;
              })}
                    </ul> : <div className="text-sm text-gray-500 italic" data-id="element-11515">
                      No emergency approvers selected
                    </div>}
                  <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-11516">
                    <div className="flex" data-id="element-11517">
                      <div className="flex-shrink-0" data-id="element-11518">
                        <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-11519" />
                      </div>
                      <div className="ml-3" data-id="element-11520">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-11521">
                          Emergency Approval Note
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700" data-id="element-11522">
                          <p data-id="element-11523">
                            Emergency approvers can short-circuit normal
                            approval flows in urgent situations. The Four-Eyes
                            principle will still be enforced if enabled.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
        {/* Line-Level Evaluation Section */}
        <div className="border-b border-gray-200" data-id="element-11524">
          <button type="button" onClick={() => toggleSection('lineLevel')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11525">
            <div className="flex items-center" data-id="element-11526">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11527" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11528">
                Line-Level Evaluation
              </h3>
            </div>
            {expandedSections.lineLevel ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11529" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11530" />}
          </button>
          {expandedSections.lineLevel && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11531">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11532">
                Configure line-level and multi-cost-centre evaluation
              </p>
              <div className="space-y-4" data-id="element-11533">
                <div className="flex items-center" data-id="element-11534">
                  <input id="enable-line-level" type="checkbox" checked={ruleData.lineLevel.enabled} onChange={e => handleLineLevelChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11535" />
                  <label htmlFor="enable-line-level" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11536">
                    Enable Line-Level Evaluation
                  </label>
                </div>
                {ruleData.lineLevel.enabled && <>
                    <div data-id="element-11537">
                      <label htmlFor="evaluation-mode" className="block text-sm font-medium text-gray-700" data-id="element-11538">
                        Evaluation Mode
                      </label>
                      <select id="evaluation-mode" value={ruleData.lineLevel.evaluationMode} onChange={e => handleLineLevelChange('evaluationMode', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11539">
                        <option value="Sequential" data-id="element-11540">Sequential</option>
                        <option value="Parallel" data-id="element-11541">Parallel</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-11542">
                        Sequential: Lines are approved in sequence. Parallel:
                        All lines can be approved simultaneously.
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-11543">
                      <input id="categories-only" type="checkbox" checked={ruleData.lineLevel.categoriesOnly} onChange={e => handleLineLevelChange('categoriesOnly', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11544" />
                      <label htmlFor="categories-only" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11545">
                        Approvers only for categories they're authorised for
                      </label>
                      <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, approvers will only see and approve lines for GL categories they are authorized for" data-id="element-11546" />
                    </div>
                  </>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-11547">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11548">
                    Line-Level Evaluation:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-11549">
                    <li data-id="element-11550">
                      Evaluates each line item separately based on its GL
                      code/cost centre
                    </li>
                    <li data-id="element-11551">
                      Allows for different approvers for different types of
                      expenses
                    </li>
                    <li data-id="element-11552">Can handle documents with multiple cost centres</li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Accounting Periods Section */}
        <div className="border-b border-gray-200" data-id="element-11553">
          <button type="button" onClick={() => toggleSection('periods')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11554">
            <div className="flex items-center" data-id="element-11555">
              <CalendarIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11556" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11557">
                Accounting Periods
              </h3>
            </div>
            {expandedSections.periods ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11558" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11559" />}
          </button>
          {expandedSections.periods && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11560">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11561">
                Configure accounting periods and closed period checks
              </p>
              <div className="flex items-center mb-4" data-id="element-11562">
                <input id="check-closed-periods" type="checkbox" checked={ruleData.preApproval.checkClosedPeriods} onChange={e => setRuleData({
              ...ruleData,
              preApproval: {
                ...ruleData.preApproval,
                checkClosedPeriods: e.target.checked
              }
            })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11563" />
                <label htmlFor="check-closed-periods" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11564">
                  Check for closed accounting periods
                </label>
              </div>
              {ruleData.preApproval.checkClosedPeriods && <div className="space-y-4" data-id="element-11565">
                  <div className="overflow-x-auto" data-id="element-11566">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-11567">
                      <thead className="bg-gray-50" data-id="element-11568">
                        <tr data-id="element-11569">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11570">
                            Entity
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11571">
                            Period Code
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11572">
                            Start Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11573">
                            End Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11574">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11575">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-11576">
                        {accountingPeriods.map((period, index) => {
                    const entity = entities.find(e => e.id === period.entityId);
                    return <tr key={period.id} data-id="element-11577">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11578">
                                {entity?.name || 'Unknown Entity'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11579">
                                {period.periodCode}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11580">
                                {new Date(period.startUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11581">
                                {new Date(period.endUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-11582">
                                {period.isClosed ? <StatusBadge status="error" text="Closed" data-id="element-11583" /> : <StatusBadge status="success" text="Open" data-id="element-11584" />}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11585">
                                <div className="flex justify-end space-x-2" data-id="element-11586">
                                  <button type="button" onClick={() => updateAccountingPeriod(index, 'isClosed', !period.isClosed)} className={period.isClosed ? 'text-green-600 hover:text-green-900' : 'text-amber-600 hover:text-amber-900'} title={period.isClosed ? 'Open Period' : 'Close Period'} data-id="element-11587">
                                    {period.isClosed ? <CheckIcon className="h-4 w-4" data-id="element-11588" /> : <LockIcon className="h-4 w-4" data-id="element-11589" />}
                                  </button>
                                  <button type="button" onClick={() => removeAccountingPeriod(index)} className="text-red-600 hover:text-red-900" title="Remove Period" data-id="element-11590">
                                    <TrashIcon className="h-4 w-4" data-id="element-11591" />
                                  </button>
                                </div>
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addAccountingPeriod} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11592">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-11593" />
                    Add Accounting Period
                  </button>
                </div>}
            </div>}
        </div>
        {/* Multi-Currency Section */}
        <div className="border-b border-gray-200" data-id="element-11594">
          <button type="button" onClick={() => toggleSection('multiCurrency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11595">
            <div className="flex items-center" data-id="element-11596">
              <DollarSignIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11597" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11598">
                Multi-Currency
              </h3>
            </div>
            {expandedSections.multiCurrency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11599" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11600" />}
          </button>
          {expandedSections.multiCurrency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11601">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11602">
                Configure multi-currency support
              </p>
              <div className="space-y-4" data-id="element-11603">
                <div className="flex items-center" data-id="element-11604">
                  <input id="enable-multi-currency" type="checkbox" checked={ruleData.multiCurrency.enabled} onChange={e => handleMultiCurrencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11605" />
                  <label htmlFor="enable-multi-currency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11606">
                    Enable Multi-Currency Support
                  </label>
                </div>
                {ruleData.multiCurrency.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-11607">
                    <div data-id="element-11608">
                      <label htmlFor="base-currency" className="block text-sm font-medium text-gray-700" data-id="element-11609">
                        Base Currency
                      </label>
                      <select id="base-currency" value={ruleData.multiCurrency.baseCurrency} onChange={e => handleMultiCurrencyChange('baseCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11610">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11611">
                            {currency.code} - {currency.name}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-11612">
                      <label htmlFor="evaluate-at" className="block text-sm font-medium text-gray-700" data-id="element-11613">
                        Evaluate Thresholds At
                      </label>
                      <select id="evaluate-at" value={ruleData.multiCurrency.evaluateAt} onChange={e => handleMultiCurrencyChange('evaluateAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11614">
                        <option value="Header" data-id="element-11615">Header (Document Total)</option>
                        <option value="Line" data-id="element-11616">Line (Line Subtotals)</option>
                      </select>
                    </div>
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-11617">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11618">
                    Multi-Currency Support:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-11619">
                    <li data-id="element-11620">
                      Converts transaction amounts to the base currency for
                      threshold evaluation
                    </li>
                    <li data-id="element-11621">Uses current exchange rates from the system</li>
                    <li data-id="element-11622">
                      Allows approvers to have limits in different currencies
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Audit Section */}
        <div className="border-b border-gray-200" data-id="element-11623">
          <button type="button" onClick={() => toggleSection('audit')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11624">
            <div className="flex items-center" data-id="element-11625">
              <ClockIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11626" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11627">Audit</h3>
            </div>
            {expandedSections.audit ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11628" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11629" />}
          </button>
          {expandedSections.audit && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11630">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11631">
                Configure audit settings
              </p>
              <div className="space-y-4" data-id="element-11632">
                <div className="flex items-center" data-id="element-11633">
                  <input id="enable-audit" type="checkbox" checked={ruleData.audit.enabled} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  enabled: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11634" />
                  <label htmlFor="enable-audit" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11635">
                    Enable Audit Logging
                  </label>
                </div>
                {ruleData.audit.enabled && <div className="flex items-center" data-id="element-11636">
                    <input id="store-decisions" type="checkbox" checked={ruleData.audit.storeDecisions} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  storeDecisions: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11637" />
                    <label htmlFor="store-decisions" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11638">
                      Store Rule Decisions
                    </label>
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, all rule decisions (Approved, Escalated, Failed, etc.) will be stored in the audit log" data-id="element-11639" />
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-11640">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11641">
                    Audit Features:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-11642">
                    <li data-id="element-11643">
                      Append-only audit log ensures all rule evaluations are
                      recorded
                    </li>
                    <li data-id="element-11644">
                      Tamper-evident hashing ensures audit trail integrity
                    </li>
                    <li data-id="element-11645">
                      Compliant with ISO27001 and other regulatory requirements
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
      </div>
      <div className="flex justify-end space-x-3" data-id="element-11646">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11647">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11648">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-11649" />
          Save Rule
        </button>
      </div>
    </form>;
}